import "../../components/EventUserDashboard.css";
import AdminBackBar from "../../components/admin/AdminBackBar";
import Billede1 from "../../Images/Billede1.jpg";
import HeroSectionV2 from "../../components/HeroSectionV2";

import React, { useEffect, useMemo, useState } from "react";

import {
    Alert,
    Badge,
    Button,
    Container,
    Divider,
    Grid,
    Group,
    Loader,
    Modal,
    NumberInput,
    Paper,
    ScrollArea,
    Select,
    Stack,
    Table,
    Text,
    TextInput,
    ThemeIcon,
    Title,
    Tooltip,
    ActionIcon,
} from "@mantine/core";

import {
    IconCheck,
    IconChevronRight,
    IconGlass,
    IconPlus,
    IconReceipt,
    IconTicket,
    IconTrash,
} from "@tabler/icons-react";

// ---------------------------------------------------------
// TYPES
// ---------------------------------------------------------

type EventDto = {
    eventID: number;
    eventName?: string;
};

type EventBar = {
    eventBarId: number;
    name: string;
    isActive: boolean;
};

type InvoiceDto = {
    invoiceId: number;
    name: string;
    isActive: boolean;
};

type DrinkTicketCollectionDto = {
    drinkTicketCollectionId: number;
    collectionName: string;
    eventBarId: number;
    maxDrinksPerTicket: number;
    ticketCount: number;
    validDate?: string | null;
};

type DrinkTicketDto = {
    drinkTicketId: number;
    ticketCode: string;
    ticketNumber: number;
    maxDrinks: number;
    isActive: boolean;
};

// ---------------------------------------------------------
// HELPERS
// ---------------------------------------------------------

const toArray = <T,>(r: any): T[] =>
    Array.isArray(r)
        ? r
        : Array.isArray(r?.data)
            ? r.data
            : Array.isArray(r?.items)
                ? r.items
                : Array.isArray(r?.results)
                    ? r.results
                    : [];

const toInt = (v: any) => {
    const n = Number(v);
    return Number.isFinite(n) ? n : 0;
};

const formatDate = (v?: string | null) => {
    if (!v) return "All days";

    const d = new Date(v);

    return Number.isNaN(d.getTime())
        ? v
        : d.toLocaleDateString("en-GB", {
            day: "2-digit",
            month: "short",
            year: "numeric",
        });
};

// ---------------------------------------------------------
// COMPONENT
// ---------------------------------------------------------

const AdminDrinkTicketCollections: React.FC = () => {
    const apiUrl = process.env.REACT_APP_API_URL || "";

    // -----------------------------------------------------
    // STATE
    // -----------------------------------------------------

    const [events, setEvents] = useState<EventDto[]>([]);
    const [eventId, setEventId] = useState<number>(0);

    const [bars, setBars] = useState<EventBar[]>([]);
    const [eventBarId, setEventBarId] = useState<number>(0);

    const [invoices, setInvoices] = useState<InvoiceDto[]>([]);
    const [invoiceId, setInvoiceId] = useState<number>(0);

    const [collectionName, setCollectionName] = useState("");
    const [ticketCount, setTicketCount] = useState(20);
    const [maxDrinksPerTicket, setMaxDrinksPerTicket] = useState(1);
    const [validDate, setValidDate] = useState("");

    const [collections, setCollections] =
        useState<DrinkTicketCollectionDto[]>([]);

    const [selectedCollectionId, setSelectedCollectionId] =
        useState<number>(0);

    const [tickets, setTickets] = useState<DrinkTicketDto[]>([]);

    const [loadingCollections, setLoadingCollections] = useState(false);
    const [loadingTickets, setLoadingTickets] = useState(false);

    const [error, setError] = useState<string | null>(null);
    const [message, setMessage] = useState<string | null>(null);

    const [saving, setSaving] = useState(false);

    // Delete modal
    const [deleteModalOpen, setDeleteModalOpen] = useState(false);

    const [collectionToDelete, setCollectionToDelete] =
        useState<DrinkTicketCollectionDto | null>(null);

    const [deleting, setDeleting] = useState(false);

    // -----------------------------------------------------
    // EVENTS
    // -----------------------------------------------------

    useEffect(() => {
        fetch(`${apiUrl}/api/events`)
            .then((r) => r.json())
            .then((d) => setEvents(toArray<EventDto>(d)))
            .catch(() => setEvents([]));
    }, [apiUrl]);

    // -----------------------------------------------------
    // BARS
    // -----------------------------------------------------

    const loadBars = async (id: number) => {
        try {
            const res = await fetch(
                `${apiUrl}/api/EventBars/event/${id}`
            );

            if (!res.ok) {
                setBars([]);
                return;
            }

            const data = await res.json();

            setBars(toArray<EventBar>(data));
        } catch {
            setBars([]);
        }
    };

    // -----------------------------------------------------
    // INVOICES
    // -----------------------------------------------------

    const loadInvoices = async (id: number) => {
        try {
            const res = await fetch(
                `${apiUrl}/api/invoices/event/${id}`
            );

            if (!res.ok) {
                setInvoices([]);
                return;
            }

            const data = await res.json();

            setInvoices(toArray<InvoiceDto>(data));
        } catch {
            setInvoices([]);
        }
    };

    // -----------------------------------------------------
    // COLLECTIONS
    // -----------------------------------------------------

    const loadCollections = async (id: number) => {
        if (!id) {
            setCollections([]);
            return;
        }

        setLoadingCollections(true);
        setError(null);

        try {
            const res = await fetch(
                `${apiUrl}/api/admin/drink-ticket-collections?eventId=${id}`
            );

            if (!res.ok) {
                throw new Error(await res.text());
            }

            const data = await res.json();

            setCollections(
                toArray<DrinkTicketCollectionDto>(data)
            );
        } catch {
            setError("Could not load collections.");
            setCollections([]);
        } finally {
            setLoadingCollections(false);
        }
    };

    // -----------------------------------------------------
    // TICKETS
    // -----------------------------------------------------

    const loadTickets = async (id: number) => {
        if (!id) {
            setTickets([]);
            return;
        }

        setLoadingTickets(true);

        try {
            const res = await fetch(
                `${apiUrl}/api/admin/drink-ticket-collections/${id}/tickets`
            );

            if (!res.ok) {
                setTickets([]);
                return;
            }

            const data = await res.json();

            setTickets(toArray<DrinkTicketDto>(data));
        } catch {
            setTickets([]);
        } finally {
            setLoadingTickets(false);
        }
    };

    // -----------------------------------------------------
    // EVENT CHANGE
    // -----------------------------------------------------

    useEffect(() => {
        setEventBarId(0);
        setInvoiceId(0);
        setSelectedCollectionId(0);

        setTickets([]);
        setBars([]);
        setInvoices([]);
        setCollections([]);

        setError(null);
        setMessage(null);

        if (!eventId) return;

        loadBars(eventId);
        loadInvoices(eventId);
        loadCollections(eventId);
    }, [eventId]);

    // -----------------------------------------------------
    // COLLECTION SELECT
    // -----------------------------------------------------

    useEffect(() => {
        if (selectedCollectionId) {
            loadTickets(selectedCollectionId);
        } else {
            setTickets([]);
        }
    }, [selectedCollectionId]);

    // -----------------------------------------------------
    // SELECTED COLLECTION
    // -----------------------------------------------------

    const selectedCollection = useMemo(
        () =>
            collections.find(
                (c) =>
                    c.drinkTicketCollectionId === selectedCollectionId
            ) || null,
        [collections, selectedCollectionId]
    );

    // -----------------------------------------------------
    // SELECTED BAR
    // -----------------------------------------------------

    const selectedBar = useMemo(
        () =>
            bars.find(
                (bar) =>
                    bar.eventBarId === selectedCollection?.eventBarId
            ) || null,
        [bars, selectedCollection]
    );

    // -----------------------------------------------------
    // HELPERS
    // -----------------------------------------------------

    const getBarName = (barId: number) => {
        return (
            bars.find((bar) => bar.eventBarId === barId)?.name ||
            `Bar ${barId}`
        );
    };

    // -----------------------------------------------------
    // CREATE COLLECTION
    // -----------------------------------------------------

    const submit = async () => {
        if (!eventId || !eventBarId || !invoiceId) {
            setError("Select event + bar + invoice.");
            return;
        }

        if (!collectionName.trim()) {
            setError("Missing collection name.");
            return;
        }

        if (ticketCount <= 0) {
            setError("Ticket count must be greater than 0.");
            return;
        }

        if (maxDrinksPerTicket <= 0) {
            setError("Maximum drinks must be greater than 0.");
            return;
        }

        setSaving(true);
        setError(null);
        setMessage(null);

        try {
            const res = await fetch(
                `${apiUrl}/api/admin/drink-ticket-collections`,
                {
                    method: "POST",

                    headers: {
                        "Content-Type": "application/json",
                    },

                    body: JSON.stringify({
                        eventId,
                        eventBarId,
                        invoiceId,
                        collectionName: collectionName.trim(),
                        ticketCount,
                        maxDrinksPerTicket,
                        validDate: validDate || null,
                    }),
                }
            );

            if (!res.ok) {
                const text = await res.text();

                throw new Error(text || "Create failed.");
            }

            setMessage("Collection created.");

            setCollectionName("");
            setValidDate("");

            await loadCollections(eventId);
        } catch (e: any) {
            setError(
                e?.message ||
                "Could not create collection."
            );
        } finally {
            setSaving(false);
        }
    };

    // -----------------------------------------------------
    // OPEN DELETE MODAL
    // -----------------------------------------------------

    const openDeleteModal = (
        collection: DrinkTicketCollectionDto
    ) => {
        setCollectionToDelete(collection);
        setDeleteModalOpen(true);
    };

    const closeDeleteModal = () => {
        if (deleting) return;

        setDeleteModalOpen(false);
        setCollectionToDelete(null);
    };

    // -----------------------------------------------------
    // DELETE COLLECTION
    // -----------------------------------------------------

    const confirmDeleteCollection = async () => {
        if (!collectionToDelete) return;

        const id =
            collectionToDelete.drinkTicketCollectionId;

        setDeleting(true);
        setError(null);
        setMessage(null);

        try {
            const res = await fetch(
                `${apiUrl}/api/admin/drink-ticket-collections/${id}`,
                {
                    method: "DELETE",
                }
            );

            const text = await res.text();

            if (!res.ok) {
                throw new Error(
                    text || "Delete failed."
                );
            }

            if (selectedCollectionId === id) {
                setSelectedCollectionId(0);
                setTickets([]);
            }

            setDeleteModalOpen(false);
            setCollectionToDelete(null);

            setMessage("Collection deleted.");

            await loadCollections(eventId);
        } catch (e: any) {
            setError(
                e?.message ||
                "Could not delete collection."
            );
        } finally {
            setDeleting(false);
        }
    };

    // -----------------------------------------------------
    // RENDER
    // -----------------------------------------------------

    return (
        <div>
            <HeroSectionV2
                titleEn="Ticket Collections"
                titleDa="Ticket Collections"
                descriptionEn="Manage drink ticket collections"
                descriptionDa="Administrer drink ticket collections"
                imageSrc={Billede1}
            />

            <AdminBackBar
                title="Drinks"
                crumbs={[
                    {
                        label: "Admin",
                        to: "/admin",
                    },
                    {
                        label: "Drinks",
                    },
                ]}
            />

            <Container size="xl" py="xl">
                <Stack gap="xl">

                    {/* ============================================= */}
                    {/* MESSAGES */}
                    {/* ============================================= */}

                    {error && (
                        <Alert
                            color="red"
                            title="Error"
                            withCloseButton
                            onClose={() => setError(null)}
                        >
                            {error}
                        </Alert>
                    )}

                    {message && (
                        <Alert
                            color="green"
                            title="Success"
                            withCloseButton
                            onClose={() => setMessage(null)}
                        >
                            {message}
                        </Alert>
                    )}

                    {/* ============================================= */}
                    {/* CREATE COLLECTION */}
                    {/* ============================================= */}

                    <Paper
                        withBorder
                        radius="lg"
                        shadow="xs"
                        style={{
                            overflow: "hidden",
                        }}
                    >
                        {/* HEADER */}

                        <Group p="lg">
                            <ThemeIcon
                                color="red"
                                variant="light"
                                size="lg"
                                radius="md"
                            >
                                <IconPlus size={20} />
                            </ThemeIcon>

                            <div>
                                <Title order={4}>
                                    Create ticket collection
                                </Title>

                                <Text
                                    size="sm"
                                    c="dimmed"
                                >
                                    Create a new collection of drink tickets
                                </Text>
                            </div>
                        </Group>

                        <Divider />

                        {/* SETUP */}

                        <Stack p="lg" gap="md">
                            <div>
                                <Text fw={700} size="sm">
                                    Setup
                                </Text>

                                <Text
                                    size="xs"
                                    c="dimmed"
                                >
                                    Select event, bar and invoice
                                </Text>
                            </div>

                            <Grid>
                                <Grid.Col
                                    span={{
                                        base: 12,
                                        md: 4,
                                    }}
                                >
                                    <Select
                                        label="Event"
                                        placeholder="Select event..."
                                        value={
                                            eventId
                                                ? String(eventId)
                                                : null
                                        }
                                        onChange={(value) =>
                                            setEventId(
                                                toInt(value)
                                            )
                                        }
                                        data={events.map(
                                            (event) => ({
                                                value: String(
                                                    event.eventID
                                                ),

                                                label:
                                                    event.eventName ||
                                                    `Event ${event.eventID}`,
                                            })
                                        )}
                                        searchable
                                        clearable
                                    />
                                </Grid.Col>

                                <Grid.Col
                                    span={{
                                        base: 12,
                                        md: 4,
                                    }}
                                >
                                    <Select
                                        label="Bar"
                                        placeholder={
                                            eventId
                                                ? "Select bar..."
                                                : "Select event first..."
                                        }
                                        disabled={!eventId}
                                        value={
                                            eventBarId
                                                ? String(eventBarId)
                                                : null
                                        }
                                        onChange={(value) =>
                                            setEventBarId(
                                                toInt(value)
                                            )
                                        }
                                        data={bars
                                            .filter(
                                                (bar) =>
                                                    bar.isActive
                                            )
                                            .map((bar) => ({
                                                value: String(
                                                    bar.eventBarId
                                                ),
                                                label: bar.name,
                                            }))}
                                        searchable
                                        clearable
                                    />
                                </Grid.Col>

                                <Grid.Col
                                    span={{
                                        base: 12,
                                        md: 4,
                                    }}
                                >
                                    <Select
                                        label="Invoice"
                                        placeholder={
                                            eventId
                                                ? "Select invoice..."
                                                : "Select event first..."
                                        }
                                        disabled={!eventId}
                                        value={
                                            invoiceId
                                                ? String(invoiceId)
                                                : null
                                        }
                                        onChange={(value) =>
                                            setInvoiceId(
                                                toInt(value)
                                            )
                                        }
                                        data={invoices
                                            .filter(
                                                (invoice) =>
                                                    invoice.isActive
                                            )
                                            .map(
                                                (invoice) => ({
                                                    value: String(
                                                        invoice.invoiceId
                                                    ),
                                                    label:
                                                        invoice.name,
                                                })
                                            )}
                                        searchable
                                        clearable
                                    />
                                </Grid.Col>
                            </Grid>
                        </Stack>

                        <Divider />

                        {/* COLLECTION DETAILS */}

                        <Stack p="lg" gap="md">
                            <div>
                                <Text fw={700} size="sm">
                                    Collection details
                                </Text>

                                <Text
                                    size="xs"
                                    c="dimmed"
                                >
                                    Configure the tickets in this collection
                                </Text>
                            </div>

                            <Grid>
                                <Grid.Col
                                    span={{
                                        base: 12,
                                        md: 5,
                                    }}
                                >
                                    <TextInput
                                        label="Collection name"
                                        placeholder="Example: VIP Tuesday"
                                        value={collectionName}
                                        onChange={(e) =>
                                            setCollectionName(
                                                e.target.value
                                            )
                                        }
                                    />
                                </Grid.Col>

                                <Grid.Col
                                    span={{
                                        base: 12,
                                        sm: 6,
                                        md: 2,
                                    }}
                                >
                                    <NumberInput
                                        label="Tickets"
                                        value={ticketCount}
                                        min={1}
                                        onChange={(value) =>
                                            setTicketCount(
                                                Number(value) || 0
                                            )
                                        }
                                    />
                                </Grid.Col>

                                <Grid.Col
                                    span={{
                                        base: 12,
                                        sm: 6,
                                        md: 2,
                                    }}
                                >
                                    <NumberInput
                                        label="Drinks / ticket"
                                        value={
                                            maxDrinksPerTicket
                                        }
                                        min={1}
                                        onChange={(value) =>
                                            setMaxDrinksPerTicket(
                                                Number(value) || 0
                                            )
                                        }
                                    />
                                </Grid.Col>

                                <Grid.Col
                                    span={{
                                        base: 12,
                                        md: 3,
                                    }}
                                >
                                    <TextInput
                                        label="Valid date"
                                        description="Leave empty for all days"
                                        type="date"
                                        value={validDate}
                                        onChange={(e) =>
                                            setValidDate(
                                                e.target.value
                                            )
                                        }
                                    />
                                </Grid.Col>
                            </Grid>
                        </Stack>

                        <Divider />

                        <Group
                            justify="space-between"
                            p="lg"
                        >
                            <Text
                                size="sm"
                                c="dimmed"
                            >
                                {ticketCount > 0 &&
                                    maxDrinksPerTicket > 0
                                    ? `${ticketCount} tickets × ${maxDrinksPerTicket} drinks = ${ticketCount *
                                    maxDrinksPerTicket
                                    } total drinks`
                                    : ""}
                            </Text>

                            <Button
                                color="red"
                                loading={saving}
                                leftSection={
                                    <IconPlus size={17} />
                                }
                                onClick={submit}
                            >
                                Create collection
                            </Button>
                        </Group>
                    </Paper>

                    {/* ============================================= */}
                    {/* COLLECTIONS + TICKETS */}
                    {/* ============================================= */}

                    {eventId > 0 && (
                        <Grid
                            gutter="xl"
                            align="stretch"
                        >
                            {/* ===================================== */}
                            {/* COLLECTION LIST */}
                            {/* ===================================== */}

                            <Grid.Col
                                span={{
                                    base: 12,
                                    lg: 5,
                                }}
                            >
                                <Paper
                                    withBorder
                                    radius="lg"
                                    shadow="xs"
                                    h="100%"
                                    style={{
                                        overflow:
                                            "hidden",
                                    }}
                                >
                                    <Group
                                        justify="space-between"
                                        p="lg"
                                    >
                                        <Group gap="sm">
                                            <ThemeIcon
                                                color="red"
                                                variant="light"
                                                size="lg"
                                                radius="md"
                                            >
                                                <IconReceipt
                                                    size={19}
                                                />
                                            </ThemeIcon>

                                            <div>
                                                <Title order={4}>
                                                    Collections
                                                </Title>

                                                <Text
                                                    size="sm"
                                                    c="dimmed"
                                                >
                                                    Select a collection to view its tickets
                                                </Text>
                                            </div>
                                        </Group>

                                        <Badge
                                            color="gray"
                                            variant="light"
                                        >
                                            {
                                                collections.length
                                            }
                                        </Badge>
                                    </Group>

                                    <Divider />

                                    {loadingCollections ? (
                                        <Stack
                                            align="center"
                                            justify="center"
                                            p={50}
                                        >
                                            <Loader
                                                color="red"
                                                size="sm"
                                            />

                                            <Text
                                                size="sm"
                                                c="dimmed"
                                            >
                                                Loading collections...
                                            </Text>
                                        </Stack>
                                    ) : collections.length ===
                                        0 ? (
                                        <Stack
                                            align="center"
                                            justify="center"
                                            p={50}
                                            gap="xs"
                                        >
                                            <ThemeIcon
                                                color="gray"
                                                variant="light"
                                                size={48}
                                                radius="xl"
                                            >
                                                <IconReceipt
                                                    size={22}
                                                />
                                            </ThemeIcon>

                                            <Text fw={600}>
                                                No collections found
                                            </Text>

                                            <Text
                                                size="sm"
                                                c="dimmed"
                                                ta="center"
                                            >
                                                Create the first ticket collection for this event.
                                            </Text>
                                        </Stack>
                                    ) : (
                                        <Stack gap={0}>
                                            {collections.map(
                                                (
                                                    collection,
                                                    index
                                                ) => {
                                                    const selected =
                                                        selectedCollectionId ===
                                                        collection.drinkTicketCollectionId;

                                                    return (
                                                        <React.Fragment
                                                            key={
                                                                collection.drinkTicketCollectionId
                                                            }
                                                        >
                                                            <div
                                                                onClick={() =>
                                                                    setSelectedCollectionId(
                                                                        collection.drinkTicketCollectionId
                                                                    )
                                                                }
                                                                style={{
                                                                    cursor: "pointer",

                                                                    background:
                                                                        selected
                                                                            ? "var(--mantine-color-red-0)"
                                                                            : undefined,

                                                                    borderLeft:
                                                                        selected
                                                                            ? "4px solid var(--mantine-color-red-6)"
                                                                            : "4px solid transparent",

                                                                    transition:
                                                                        "background 120ms ease",
                                                                }}
                                                            >
                                                                <Group
                                                                    justify="space-between"
                                                                    align="center"
                                                                    p="md"
                                                                    wrap="nowrap"
                                                                >
                                                                    <Group
                                                                        gap="md"
                                                                        wrap="nowrap"
                                                                        style={{
                                                                            minWidth: 0,
                                                                        }}
                                                                    >
                                                                        <ThemeIcon
                                                                            color={
                                                                                selected
                                                                                    ? "red"
                                                                                    : "gray"
                                                                            }
                                                                            variant="light"
                                                                            radius="md"
                                                                            size="lg"
                                                                        >
                                                                            <IconTicket
                                                                                size={
                                                                                    18
                                                                                }
                                                                            />
                                                                        </ThemeIcon>

                                                                        <div
                                                                            style={{
                                                                                minWidth: 0,
                                                                            }}
                                                                        >
                                                                            <Group
                                                                                gap="xs"
                                                                                wrap="nowrap"
                                                                            >
                                                                                <Text
                                                                                    fw={
                                                                                        600
                                                                                    }
                                                                                    truncate
                                                                                >
                                                                                    {
                                                                                        collection.collectionName
                                                                                    }
                                                                                </Text>

                                                                                {selected && (
                                                                                    <Badge
                                                                                        color="red"
                                                                                        variant="light"
                                                                                        size="xs"
                                                                                    >
                                                                                        Selected
                                                                                    </Badge>
                                                                                )}
                                                                            </Group>

                                                                            <Text
                                                                                size="sm"
                                                                                c="dimmed"
                                                                                mt={
                                                                                    2
                                                                                }
                                                                            >
                                                                                {
                                                                                    collection.ticketCount
                                                                                }{" "}
                                                                                tickets
                                                                                {" · "}
                                                                                {
                                                                                    collection.maxDrinksPerTicket
                                                                                }{" "}
                                                                                drinks each
                                                                            </Text>

                                                                            <Text
                                                                                size="xs"
                                                                                c="dimmed"
                                                                                mt={
                                                                                    2
                                                                                }
                                                                            >
                                                                                {getBarName(
                                                                                    collection.eventBarId
                                                                                )}
                                                                                {" · "}
                                                                                {formatDate(
                                                                                    collection.validDate
                                                                                )}
                                                                            </Text>
                                                                        </div>
                                                                    </Group>

                                                                    <Group
                                                                        gap={
                                                                            4
                                                                        }
                                                                        wrap="nowrap"
                                                                    >
                                                                        <Tooltip label="Delete collection">
                                                                            <ActionIcon
                                                                                variant="subtle"
                                                                                color="red"
                                                                                size="lg"
                                                                                onClick={(
                                                                                    e
                                                                                ) => {
                                                                                    e.stopPropagation();

                                                                                    openDeleteModal(
                                                                                        collection
                                                                                    );
                                                                                }}
                                                                            >
                                                                                <IconTrash
                                                                                    size={
                                                                                        17
                                                                                    }
                                                                                />
                                                                            </ActionIcon>
                                                                        </Tooltip>

                                                                        <IconChevronRight
                                                                            size={
                                                                                17
                                                                            }
                                                                            color="var(--mantine-color-gray-5)"
                                                                        />
                                                                    </Group>
                                                                </Group>
                                                            </div>

                                                            {index <
                                                                collections.length -
                                                                1 && (
                                                                    <Divider />
                                                                )}
                                                        </React.Fragment>
                                                    );
                                                }
                                            )}
                                        </Stack>
                                    )}
                                </Paper>
                            </Grid.Col>

                            {/* ===================================== */}
                            {/* TICKETS */}
                            {/* ===================================== */}

                            <Grid.Col
                                span={{
                                    base: 12,
                                    lg: 7,
                                }}
                            >
                                <Paper
                                    withBorder
                                    radius="lg"
                                    shadow="xs"
                                    h="100%"
                                    style={{
                                        overflow:
                                            "hidden",
                                    }}
                                >
                                    {!selectedCollection ? (
                                        <Stack
                                            align="center"
                                            justify="center"
                                            p={70}
                                            gap="xs"
                                            style={{
                                                minHeight: 300,
                                            }}
                                        >
                                            <ThemeIcon
                                                color="gray"
                                                variant="light"
                                                size={54}
                                                radius="xl"
                                            >
                                                <IconTicket
                                                    size={25}
                                                />
                                            </ThemeIcon>

                                            <Text fw={600}>
                                                Select a collection
                                            </Text>

                                            <Text
                                                size="sm"
                                                c="dimmed"
                                                ta="center"
                                            >
                                                Select a collection on the left to view its tickets.
                                            </Text>
                                        </Stack>
                                    ) : (
                                        <>
                                            {/* TICKET HEADER */}

                                            <Group
                                                justify="space-between"
                                                p="lg"
                                            >
                                                <Group gap="sm">
                                                    <ThemeIcon
                                                        color="red"
                                                        variant="light"
                                                        size="lg"
                                                        radius="md"
                                                    >
                                                        <IconTicket
                                                            size={
                                                                19
                                                            }
                                                        />
                                                    </ThemeIcon>

                                                    <div>
                                                        <Group gap="xs">
                                                            <Title
                                                                order={
                                                                    4
                                                                }
                                                            >
                                                                Tickets
                                                            </Title>

                                                            <Badge
                                                                color="red"
                                                                variant="light"
                                                            >
                                                                {
                                                                    selectedCollection.ticketCount
                                                                }
                                                            </Badge>
                                                        </Group>

                                                        <Text
                                                            size="sm"
                                                            c="dimmed"
                                                        >
                                                            {
                                                                selectedCollection.collectionName
                                                            }
                                                        </Text>
                                                    </div>
                                                </Group>
                                            </Group>

                                            <Divider />

                                            {/* SUMMARY */}

                                            <Group
                                                p="md"
                                                gap="xl"
                                                style={{
                                                    background:
                                                        "var(--mantine-color-gray-0)",
                                                }}
                                            >
                                                <div>
                                                    <Text
                                                        size="xs"
                                                        c="dimmed"
                                                    >
                                                        Bar
                                                    </Text>

                                                    <Text
                                                        size="sm"
                                                        fw={600}
                                                    >
                                                        {selectedBar?.name ||
                                                            getBarName(
                                                                selectedCollection.eventBarId
                                                            )}
                                                    </Text>
                                                </div>

                                                <div>
                                                    <Text
                                                        size="xs"
                                                        c="dimmed"
                                                    >
                                                        Drinks / ticket
                                                    </Text>

                                                    <Text
                                                        size="sm"
                                                        fw={600}
                                                    >
                                                        {
                                                            selectedCollection.maxDrinksPerTicket
                                                        }
                                                    </Text>
                                                </div>

                                                <div>
                                                    <Text
                                                        size="xs"
                                                        c="dimmed"
                                                    >
                                                        Valid date
                                                    </Text>

                                                    <Text
                                                        size="sm"
                                                        fw={600}
                                                    >
                                                        {formatDate(
                                                            selectedCollection.validDate
                                                        )}
                                                    </Text>
                                                </div>
                                            </Group>

                                            <Divider />

                                            {/* TICKET CONTENT */}

                                            {loadingTickets ? (
                                                <Stack
                                                    align="center"
                                                    p={50}
                                                >
                                                    <Loader
                                                        color="red"
                                                        size="sm"
                                                    />

                                                    <Text
                                                        size="sm"
                                                        c="dimmed"
                                                    >
                                                        Loading tickets...
                                                    </Text>
                                                </Stack>
                                            ) : tickets.length ===
                                                0 ? (
                                                <Stack
                                                    align="center"
                                                    p={50}
                                                    gap="xs"
                                                >
                                                    <ThemeIcon
                                                        color="gray"
                                                        variant="light"
                                                        size={48}
                                                        radius="xl"
                                                    >
                                                        <IconTicket
                                                            size={
                                                                22
                                                            }
                                                        />
                                                    </ThemeIcon>

                                                    <Text fw={600}>
                                                        No tickets found
                                                    </Text>
                                                </Stack>
                                            ) : (
                                                <ScrollArea>
                                                    <Table
                                                        verticalSpacing="sm"
                                                        horizontalSpacing="lg"
                                                        highlightOnHover
                                                    >
                                                        <Table.Thead>
                                                            <Table.Tr>
                                                                <Table.Th>
                                                                    Code
                                                                </Table.Th>

                                                                <Table.Th>
                                                                    #
                                                                </Table.Th>

                                                                <Table.Th>
                                                                    Drinks
                                                                </Table.Th>

                                                                <Table.Th>
                                                                    Status
                                                                </Table.Th>
                                                            </Table.Tr>
                                                        </Table.Thead>

                                                        <Table.Tbody>
                                                            {tickets.map(
                                                                (
                                                                    ticket
                                                                ) => (
                                                                    <Table.Tr
                                                                        key={
                                                                            ticket.drinkTicketId
                                                                        }
                                                                    >
                                                                        <Table.Td>
                                                                            <Text
                                                                                fw={
                                                                                    600
                                                                                }
                                                                                size="sm"
                                                                                style={{
                                                                                    fontFamily:
                                                                                        "monospace",
                                                                                }}
                                                                            >
                                                                                {
                                                                                    ticket.ticketCode
                                                                                }
                                                                            </Text>
                                                                        </Table.Td>

                                                                        <Table.Td>
                                                                            <Text size="sm">
                                                                                {
                                                                                    ticket.ticketNumber
                                                                                }
                                                                            </Text>
                                                                        </Table.Td>

                                                                        <Table.Td>
                                                                            <Text size="sm">
                                                                                {
                                                                                    ticket.maxDrinks
                                                                                }
                                                                            </Text>
                                                                        </Table.Td>

                                                                        <Table.Td>
                                                                            <Badge
                                                                                color={
                                                                                    ticket.isActive
                                                                                        ? "green"
                                                                                        : "gray"
                                                                                }
                                                                                variant="light"
                                                                            >
                                                                                {ticket.isActive
                                                                                    ? "Active"
                                                                                    : "Used"}
                                                                            </Badge>
                                                                        </Table.Td>
                                                                    </Table.Tr>
                                                                )
                                                            )}
                                                        </Table.Tbody>
                                                    </Table>
                                                </ScrollArea>
                                            )}
                                        </>
                                    )}
                                </Paper>
                            </Grid.Col>
                        </Grid>
                    )}
                </Stack>
            </Container>

            {/* ===================================================== */}
            {/* DELETE MODAL */}
            {/* ===================================================== */}

            <Modal
                opened={deleteModalOpen}
                onClose={closeDeleteModal}
                centered
                radius="lg"
                title={
                    <Group gap="sm">
                        <ThemeIcon
                            color="red"
                            variant="light"
                            radius="md"
                        >
                            <IconTrash size={17} />
                        </ThemeIcon>

                        <Text fw={700}>
                            Delete collection?
                        </Text>
                    </Group>
                }
            >
                <Stack gap="lg">
                    <div>
                        <Text>
                            Are you sure you want to delete{" "}
                            <Text
                                component="span"
                                fw={700}
                            >
                                {collectionToDelete?.collectionName}
                            </Text>
                            ?
                        </Text>

                        <Text
                            size="sm"
                            c="dimmed"
                            mt="xs"
                        >
                            This permanently deletes the collection and all tickets in it.
                            This cannot be undone.
                        </Text>
                    </div>

                    {collectionToDelete && (
                        <Paper
                            withBorder
                            radius="md"
                            p="md"
                        >
                            <Group gap="md">
                                <ThemeIcon
                                    color="red"
                                    variant="light"
                                    size="lg"
                                >
                                    <IconTicket size={18} />
                                </ThemeIcon>

                                <div>
                                    <Text
                                        fw={600}
                                        size="sm"
                                    >
                                        {
                                            collectionToDelete.collectionName
                                        }
                                    </Text>

                                    <Text
                                        size="xs"
                                        c="dimmed"
                                    >
                                        {
                                            collectionToDelete.ticketCount
                                        }{" "}
                                        tickets ·{" "}
                                        {
                                            collectionToDelete.maxDrinksPerTicket
                                        }{" "}
                                        drinks each
                                    </Text>
                                </div>
                            </Group>
                        </Paper>
                    )}

                    <Group justify="flex-end">
                        <Button
                            variant="default"
                            disabled={deleting}
                            onClick={closeDeleteModal}
                        >
                            Cancel
                        </Button>

                        <Button
                            color="red"
                            loading={deleting}
                            leftSection={
                                <IconTrash size={16} />
                            }
                            onClick={
                                confirmDeleteCollection
                            }
                        >
                            Delete collection
                        </Button>
                    </Group>
                </Stack>
            </Modal>
        </div>
    );
};

export default AdminDrinkTicketCollections;